import * as React from "react"

import { cn } from "../../lib/utils"

type EmptyMediaVariantProps = {
  variant?: "default" | "icon"
}

const emptyStyles = {
  root:
    "flex min-w-0 flex-1 flex-col items-center justify-center gap-4 text-balance rounded-xl p-6 text-center w-full",
  header: "flex max-w-sm flex-col items-center gap-2",
  media:
    "mb-2 flex shrink-0 items-center justify-center [&_svg]:shrink-0",
  title: "text-sm font-normal leading-sm",
  description:
    "text-muted-foreground [&>a]:underline-offset-4 [&>a:hover]:text-primary",
  content:
    "flex w-full min-w-0 max-w-sm flex-col items-center gap-2.5 text-balance text-sm leading-sm",
}

const emptyMediaVariants: Record<
  NonNullable<EmptyMediaVariantProps["variant"]>,
  string
> = {
  default: "bg-transparent",
  icon: "size-8 rounded-lg bg-muted text-fg [&_svg:not([class*='size-'])]:size-4",
}

function Empty({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty"
      className={cn(emptyStyles.root, className)}
      {...props}
    />
  )
}

function EmptyHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-header"
      className={cn(emptyStyles.header, className)}
      {...props}
    />
  )
}

function EmptyMedia({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & EmptyMediaVariantProps) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyStyles.media, emptyMediaVariants[variant], className)}
      {...props}
    />
  )
}

function EmptyTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-title"
      className={cn(emptyStyles.title, className)}
      {...props}
    />
  )
}

function EmptyDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <div
      data-slot="empty-description"
      className={cn(emptyStyles.description, className)}
      {...props}
    />
  )
}

function EmptyContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="empty-content"
      className={cn(emptyStyles.content, className)}
      {...props}
    />
  )
}

export {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
}
